import { createAsyncThunk } from '@reduxjs/toolkit';
import { useCustomAxios } from '../../shared/lib/customAxios';
import { API_ENDPOINTS } from '../../shared/constants/constants';
import {
    OAuthUrlRequest,
    OAuthUrlResponse,
    OAuthCallbackRequest,
    OAuthCallbackResponse,
    GoogleOAuthRegistrationRequest,
    GoogleOAuthRegistrationResponse
} from '../../shared/models/oauth';

export const getGoogleOAuthUrl = createAsyncThunk(
    'oauth/getGoogleOAuthUrl',
    async (reqBody: OAuthUrlRequest, { rejectWithValue }) => {
        const { axiosGet } = useCustomAxios();

        try {
            const response = await axiosGet(API_ENDPOINTS.getGoogleOAuthUrl);
            return {
                data: response.data as OAuthUrlResponse,
                statusCode: response.status,
            };
        } catch (error: any) {
            return rejectWithValue({
                error: error.response?.data?.message || 'Failed to get OAuth URL',
                statusCode: error.response?.status || 500,
            });
        }
    }
);

// Unified OAuth signin action that follows the same pattern as regular signin
export const oauthSigninApi = createAsyncThunk(
    'user/oauthSignin',
    async (reqBody: OAuthCallbackRequest, { rejectWithValue }) => {
        const { axiosPost, axiosGet } = useCustomAxios();

        try {
            console.log('OAuth Signin API - Request:', reqBody);
            const response = await axiosPost(API_ENDPOINTS.oauthCallback, reqBody);
            console.log('OAuth Signin API - Response:', response);

            // Check if registration is required (status 202) - treat as successful flow
            if (response.status === 202 || response.data?.requiresRegistration) {
                console.log('OAuth - Registration required');
                const registrationResponse = {
                    data: {
                        requiresRegistration: true,
                        userInfo: response.data?.userInfo,
                        source: response.data?.source,
                        message: response.data?.message || 'Registration required to complete OAuth signin',
                    },
                    statusCode: response.status,
                };
                return registrationResponse;
            }

            // For successful authentication, get the login response from the backend
            console.log('OAuth - Success response structure:', response.data);

            // Backend returns: {success: true, loginResponse: {...}, isGoogleOAuthUser: true, message: '...'}
            const loginResponse = response.data.loginResponse;

            if (!loginResponse) {
                throw new Error('Login response not found in OAuth callback response');
            }

            // Return data in the same format as regular signin
            return {
                data: {
                    ...loginResponse,
                    location: [], // Consistent with regular signin
                    userName: loginResponse.party?.details?.name ||
                        loginResponse.party?.details?.groupName ||
                        'Unknown User',
                    isGoogleOAuthUser: true,
                },
                statusCode: response.status,
            };
        } catch (error: any) {
            console.error('OAuth Signin API - Error:', error);
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'OAuth authentication failed';

            return rejectWithValue({
                error: errorMessage,
                statusCode: error.response?.status || 500,
                details: error.response?.data || null,
            });
        }
    }
);

// Keep the original handleOAuthCallback for backward compatibility (if needed)
export const handleOAuthCallback = createAsyncThunk(
    'oauth/handleCallback',
    async (reqBody: OAuthCallbackRequest, { rejectWithValue }) => {
        const { axiosPost, axiosGet } = useCustomAxios();

        try {
            const response = await axiosPost(API_ENDPOINTS.oauthCallback, reqBody);

            // Check if registration is required (status 202)
            if (response.status === 202 || response.data?.requiresRegistration) {
                // Return the registration required response without fetching user details
                return {
                    data: response.data as OAuthCallbackResponse,
                    statusCode: response.status,
                };
            }

            // Get user details for successful authentication
            const defaultConfig = {
                headers: {
                    Authorization: `Bearer ${response?.data?.authorizationToken}`,
                    "x-source": "SOCIETYPORTAL"
                },
            };

            const userDetails = await axiosGet(
                `/party/${response.data.party._id}`,
                defaultConfig
            );

            return {
                data: {
                    ...response.data,
                    userName:
                        userDetails?.data?.details?.name ||
                        userDetails?.data?.details?.groupName,
                } as OAuthCallbackResponse,
                statusCode: response.status,
            };
        } catch (error: any) {
            console.error('Handle OAuth Callback - Error:', error);
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'OAuth authentication failed';

            return rejectWithValue({
                error: errorMessage,
                statusCode: error.response?.status || 500,
                details: error.response?.data || null,
            });
        }
    }
);

export const registerWithGoogleOAuth = createAsyncThunk(
    'oauth/registerWithGoogleOAuth',
    async (reqBody: GoogleOAuthRegistrationRequest, { rejectWithValue }) => {
        const { axiosPost } = useCustomAxios();

        try {
            const response = await axiosPost('/auth/google/register', reqBody);

            return {
                data: response.data as GoogleOAuthRegistrationResponse,
                statusCode: response.status,
            };
        } catch (error: any) {
            return rejectWithValue({
                error: error.response?.data?.message || 'Registration failed',
                statusCode: error.response?.status || 500,
            });
        }
    }
); 